/* eslint-disable complexity */
import React, {
  ReactNode,
  RefObject,
  TextareaHTMLAttributes,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';

import { useRows } from '~/components/chat';
import { ChatModesButtonGroup } from '~/components/chat-landing/ChatModesButtonGroup';
import { appPaths } from '~/routes/app-paths';
import { Statistics } from '~/views/alfred/document-search/document-sources-view/document-sources-table/use-document-sources-table-store/useDocumentSourcesTableStore';
import { useDocumentSearch } from '~/views/alfred/document-search/use-document-search/useDocumentSearch';
import {
  ChatMode,
  useChatNavigation,
} from '~/views/alfred/use-chat-navigation/useChatNavigation';

import { ChatInputContextHeader } from '../chat-input-context-header/ChatInputContextHeader';
import { AnalysisModeDropdown } from './AnalysisModeDropdown';
import * as S from './ChatDocumentContextInput.styles';
import { WebSearchDropdown } from './web-search/WebSearchDropdown';

export interface ChatDocumentContextInputProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onKeyDown'> {
  children?: ReactNode;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  isMessagePending: boolean;
  contextCounts?: {
    documentsCount: number;
    subTypeCount: number;
    statistics?: Statistics;
  };
  allowedModes: ChatMode[];
  onKeyDownProp?: React.KeyboardEventHandler<HTMLTextAreaElement>;
}

export const ChatDocumentContextInput = ({
  children,
  value,
  isMessagePending,
  inputRef,
  contextCounts,
  allowedModes,
  autoFocus,
  placeholder,
  disabled,
  onChange,
  onKeyDownProp,
}: ChatDocumentContextInputProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { rows, updateRows } = useRows({
    value: value,
    textAreaRef: inputRef,
  });

  const { mode } = useChatNavigation();
  const location = useLocation();
  const isAlfredPage = location.pathname === appPaths.base;
  const hasActiveDocumentMessage = useDocumentSearch(
    state => !state.isMessagePending && !!state.messages.length
  );

  const [showTools, setShowTools] = useState(isAlfredPage ? true : false);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateRows(event);
    onChange?.(event);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = e => {
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.stopPropagation();
    } else {
      onKeyDownProp?.(e);
    }
  };

  const showToolsOnNonAlfredPage = !isAlfredPage && showTools;
  const showToolsOnAlfredPage = isAlfredPage && mode === 'research';

  const showChildrenInTextArea =
    (isAlfredPage && mode !== 'research') || (!isAlfredPage && !showTools);
  const showChildrenInTools =
    !isAlfredPage || (isAlfredPage && mode === 'research');

  return (
    <S.ChatInputFieldWrapper $isMessagePending={isMessagePending}>
      <S.ChatInputFieldContainer ref={wrapperRef}>
        {mode === 'document' && isAlfredPage && hasActiveDocumentMessage && (
          <ChatInputContextHeader contextCounts={contextCounts} />
        )}
        <S.TextAreaWrapper>
          <S.TextareaElement
            id="dropdown-trigger"
            as="textarea"
            ref={inputRef}
            onChange={handleChange}
            rows={rows}
            value={value}
            autoFocus={autoFocus}
            placeholder={placeholder}
            disabled={disabled}
            onFocus={() => {
              setShowTools(true);
            }}
            data-testid="chat-input"
            {...(mode === 'research'
              ? { onKeyDown }
              : { onKeyDown: onKeyDownProp })}
          />
          {showChildrenInTextArea && children}
        </S.TextAreaWrapper>
        {(showToolsOnNonAlfredPage || showToolsOnAlfredPage) && (
          <S.AlfredTools>
            <S.ToolsWrapper
              onClick={e => {
                e.preventDefault();
              }}
            >
              <S.LeftTools>
                {!isAlfredPage && allowedModes.length > 1 && (
                  <ChatModesButtonGroup compactMode variant="secondary" />
                )}
                {mode === 'research' && (
                  <S.ResearchDropdowns>
                    <AnalysisModeDropdown isMessagePending={isMessagePending} />
                    <S.ResearchDropdownsDivider>
                      <WebSearchDropdown />
                    </S.ResearchDropdownsDivider>
                  </S.ResearchDropdowns>
                )}
              </S.LeftTools>
            </S.ToolsWrapper>
            {showChildrenInTools && children}
          </S.AlfredTools>
        )}
      </S.ChatInputFieldContainer>
    </S.ChatInputFieldWrapper>
  );
};

ChatDocumentContextInput.Styled = S;
